import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type ViewEventData } from '@databases/analytics/src/types';
import { messages as viewSettingsMessages } from '@databases/api-views/messages';
import { useDialog } from '@databases/dialog';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { useEffect, useRef } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import useView from './useView';
import useViewPanel from './useViewPanel';

const useDeleteView = (contentId: string) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const { addFlag, showFlag } = useFlags();

  const { closeViewSettingsPanel } = useViewPanel();
  const { view, setViewId, resolvedView } = useView();
  const { showDialog } = useDialog();

  const intl = useIntl();
  const { formatMessage } = intl;

  const viewCount = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.views.length;
  });

  const viewCountRef = useRef(viewCount);

  useEffect(() => {
    viewCountRef.current = viewCount;
  }, [viewCount]);

  return useCallback(
    (viewId: string, source: ViewEventData['source'], onCloseAdditional?: () => void) => {
      showDialog('ConfirmDialog', {
        heading: formatMessage(messages['toolbar-views.delete-view-heading']),
        // @remark: Disabled as part of https://bitbucket.org/atlassian/canvas/pull-requests/27504/overview,
        // this should be re-enabled as soon as feasible
        // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
        body: <p>{formatMessage(messages['toolbar-views.delete-view-text'])}</p>,
        yesLabel: formatMessage(
          viewSettingsMessages['view-actions.delete-view-delete-button']
        ),
        noLabel: formatMessage(messages['toolbar-views.delete-view-cancel-button']),
        appearance: 'danger',
        onClose: (result) => {
          const currentViewCount = viewCountRef.current;

          if (currentViewCount < 2) {
            const baseFlagData = {
              title: formatMessage(
                messages['toolbar-views.delete-view-failure-flag-title']
              ),
              description: formatMessage(
                messages['toolbar-views.delete-view-failure-flag-description']
              ),
              icon: () => (
                <StatusErrorIcon
                  spacing="spacious"
                  label=""
                  color={token('color.text.danger')}
                />
              ),
            };

            if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
              showFlag({
                ...baseFlagData,
                id: 'delete-view-failure-flag',
                type: 'error',
              });
            } else {
              addFlag(baseFlagData);
            }
            return;
          }

          // Focus the menu trigger button after dialog closes
          setTimeout(() => {
            const triggerButton = document.querySelector(
              '.view-actions-menu-trigger-button'
            );
            if (triggerButton instanceof HTMLElement) {
              triggerButton.focus();
            }
          }, 0);

          if (result) {
            void client.sendViewDeletedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              source: source,
              viewId: viewId,
            });

            setViewId(resolvedView.id);
            if (view.id === viewId) {
              closeViewSettingsPanel();
            }
            dispatch(
              addChangeToDraft(contentId, 'main', {
                type: DatabaseChangeType.DELETE_SAVED_VIEW,
                viewId: viewId,
              })
            );

            onCloseAdditional && onCloseAdditional();
          }
        },
      });
    },
    [
      showDialog,
      formatMessage,
      addFlag,
      showFlag,
      client,
      contentId,
      setViewId,
      resolvedView.id,
      view.id,
      dispatch,
      closeViewSettingsPanel,
    ]
  );
};

export default useDeleteView;
